import React, { useCallback, useMemo, useState } from "react";
import PropTypes from "prop-types";
import toast from "react-hot-toast";
import InputDatePicker from "../../../../EvaluationNew/components/InputDatePicker";
import FormSignatureSections from "../FormSignatureSections";
import useFormSignatures from "../useFormSignatures";
import {
  CheckboxCard,
  FORM_FLAG_MESSAGES,
  Field,
  FormActionBar,
  FormIdentityStrip,
  FormLoadingSkeleton,
  FormMetaRow,
  FormPageHeader,
  LockedReasonBanner,
  OfficeCommentsNote,
  OfficeReviewSection,
  SHEET,
  Section,
  SubHeading,
  checkboxClass,
  fieldClass,
} from "../formLayout";
import {
  createPatientClinicalForm,
  getPatientConsentForm,
  updatePatientConsentForm,
} from "../../../../../services/patientService";
import {
  CalendarIcon,
  MapPinIcon,
  MedicalNotesIcon,
  ShieldCheckIcon,
} from "../../../../../Icons";
import { displayDateUS, formatDateForInput } from "../../../../../utils/dateUtils";
import { PATIENT_PAGE_SHELL } from "../../../patientPageLayout";
import { saveDedicatedForm } from "../clinicalFormDraft";
import {
  draftCaregiverIdentity,
  useFormCaregiverPicker,
} from "../formCaregiver";
import { buildClinicalFormPermissions } from "../clinicalFormPermissions";
import { useClinicalCosignPermissions } from "../hooks/useClinicalCosignPermissions";
import { resolveFormActorRole } from "../disciplineFormAccess";
import {
  useDedicatedFormLoadEffect,
  useSessionUser,
} from "../useDedicatedFormLoadEffect";
import {
  ACKNOWLEDGEMENTS,
  ADVANCE_BENEFICIARY_NOTICE,
  BILLING_CONFIGURATIONS,
  PRIMARY_AUTHORIZATIONS,
  VISIT_FREQUENCY_ROWS,
} from "./patientConsentContent";

/** A consent is signed on the day it is given, never scheduled ahead. */
const CONSENT_DATE_MAX = new Date().toISOString().slice(0, 10);
const today = () => new Date().toISOString().slice(0, 10);

const PatientConsentForm = ({
  patient,
  clinicalForm,
  draft = null,
  onBack,
  onCreated,
  printMode = false,
  onReady,
}) => {
  const patientId = clinicalForm.patientId;
  const formId = clinicalForm.id || null;
  const isDraft = !formId;

  const [form, setForm] = useState(null);
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  const signatures = useFormSignatures();
  const { reset: resetSignatures } = signatures;

  const currentUser = useSessionUser();
  const role = resolveFormActorRole(currentUser);

  const patientName = useMemo(
    () => [patient?.lastName, patient?.firstName].filter(Boolean).join(", "),
    [patient],
  );

  const hydrate = useCallback(
    (data) => {
      setForm(data);
      setValues({
        // A consent is dated the day it is taken, so an unset date starts today.
        consentDate: data.consentDate || today(),

        companyName: data.companyName || "",
        companyAddress: data.companyAddress || "",
        city: data.city || "",
        state: data.state || "",
        zipCode: data.zipCode || "",
        phone: data.phone || "",
        fax: data.fax || "",

        consentTreatmentAdmission: Boolean(data.consentTreatmentAdmission),
        consentDiscloseInformation: Boolean(data.consentDiscloseInformation),
        consentAssignmentOfBenefits: Boolean(data.consentAssignmentOfBenefits),
        consentAdvanceBeneficiaryNotice: Boolean(
          data.consentAdvanceBeneficiaryNotice,
        ),
        billingConfiguration: data.billingConfiguration || "",

        freqSkilledNursing: data.freqSkilledNursing || "",
        freqPhysicalTherapy: data.freqPhysicalTherapy || "",
        freqOccupationalTherapy: data.freqOccupationalTherapy || "",
        freqSpeechTherapy: data.freqSpeechTherapy || "",
        freqHomeHealthAide: data.freqHomeHealthAide || "",
        freqMedicalSocialWorker: data.freqMedicalSocialWorker || "",

        ackPatientRights: Boolean(data.ackPatientRights),
        ackHipaaOasisPrivacy: Boolean(data.ackHipaaOasisPrivacy),
        ackStateHotlines: Boolean(data.ackStateHotlines),
        ackGrievanceProcess: Boolean(data.ackGrievanceProcess),
        ackPlanOfCareReviewed: Boolean(data.ackPlanOfCareReviewed),
        ackSafetyEmergencyPlan: Boolean(data.ackSafetyEmergencyPlan),
        ackReceivedRightsCopy: Boolean(data.ackReceivedRightsCopy),
        ackReceivedPrivacyNotice: Boolean(data.ackReceivedPrivacyNotice),

        patientSignatureName:
          data.patientSignatureName || data.patientName || patientName,
        clinicianSignatureName: data.clinicianSignatureName || "",
        reviewComments: data.reviewComments || "",
      });
      resetSignatures();
    },
    [patientName, resetSignatures],
  );

  useDedicatedFormLoadEffect(patientId, formId, isDraft, async (isCancelled) => {
    setLoading(true);

    if (isDraft) {
      hydrate({
        formName: clinicalForm.formName || draft?.formName || "Patient Consent",
        formType: clinicalForm.formType || draft?.formType,
        formSection: clinicalForm.formSection || draft?.formSection,
        status: "pending",
        patientName,
        ...draftCaregiverIdentity(currentUser, role),
        permissions: buildClinicalFormPermissions("pending", role),
      });
      if (!isCancelled()) {
        setLoading(false);
        onReady?.();
      }
      return;
    }

    try {
      const res = await getPatientConsentForm(patientId, formId);
      if (isCancelled()) return;
      hydrate(res.result?.form || res.form);
    } catch (err) {
      if (isCancelled()) return;
      toast.error(err.message || "Failed to load form");
      onBack();
    } finally {
      if (isCancelled()) return;
      setLoading(false);
      onReady?.();
    }
  });

  const setValue = (key, value) =>
    setValues((prev) => ({ ...prev, [key]: value }));

  // Printing renders the identical sheet, only frozen: no editing, no
  // signing, no action buttons.
  const { permissions } = useClinicalCosignPermissions({
    status: form?.status || "pending",
    role,
    actorUserId: currentUser?.id ? Number(currentUser.id) : null,
    formFilledByUserId: form?.filledBy || clinicalForm?.filledBy || (currentUser?.id ? Number(currentUser.id) : null),
    formFilledByRole: form?.filledByRole || clinicalForm?.filledByRole || role,
    serverPermissions: form?.permissions,
  });
  const canEdit = !printMode && Boolean(permissions.canEdit);
  const isReviewer = Boolean(permissions.canReview);
  const showPatientSignature = Boolean(permissions.canViewPatientSignature);
  /** Only the assigned clinician captures signatures; Admin/OA just read them. */
  const canSign = !printMode && Boolean(permissions.canSignSignatures);

  const {
    caregiverSlot,
    filledByUserId,
    assertCaregiverForCreate,
  } = useFormCaregiverPicker({
    patientId,
    formType: form?.formType || draft?.formType || clinicalForm?.formType,
    role,
    canEdit,
    preferredStaffId: draft?.visit?.staffId ?? null,
    currentFilledByUserId:
      form?.filledBy || clinicalForm?.filledBy || null,
  });

  const handleClinicianSign = (signature, signerName) => {
    signatures.handleClinicianSign(signature);
    setValues((prev) => ({
      ...prev,
      clinicianSignatureName: signerName || prev.clinicianSignatureName,
    }));
  };

  const buildPayload = (flag) => {
    const payload = { ...values, flag };
    payload.consentDate = values.consentDate
      ? formatDateForInput(values.consentDate)
      : null;
    payload.billingConfiguration = values.billingConfiguration || null;

    // Admin/OA never sign, so signature keys are only sent by the clinician.
    return canSign
      ? { ...payload, ...signatures.buildSignaturePayload() }
      : payload;
  };

  const submit = async (flag) => {
    if (flag === "to_be_corrected" && !values.reviewComments?.trim()) {
      toast.error("Add review comments before marking the form to be corrected");
      return;
    }
    const caregiverError = assertCaregiverForCreate();
    if (caregiverError) {
      toast.error(caregiverError);
      return;
    }
    setSaving(flag);
    try {
      const { form: saved, createdClinicalFormId } = await saveDedicatedForm({
        patientId,
        clinicalForm,
        draft,
        flag,
        payload: buildPayload(flag),
        createClinicalForm: createPatientClinicalForm,
        updateDedicatedForm: updatePatientConsentForm,
        filledByUserId,
        scheduleDate: values.consentDate
          ? formatDateForInput(values.consentDate)
          : null,
      });
      hydrate(saved);
      toast.success(FORM_FLAG_MESSAGES[flag] || "Form updated");
      if (createdClinicalFormId) onCreated?.(createdClinicalFormId);
    } catch (err) {
      toast.error(err.message || "Failed to save form");
    } finally {
      setSaving(null);
    }
  };

  if (loading) return <FormLoadingSkeleton />;

  if (!form) return null;

  const signatureProps = signatures.sectionProps(form);
  const abnAccepted = Boolean(values.consentAdvanceBeneficiaryNotice);

  return (
    <div className={PATIENT_PAGE_SHELL}>
      {/* Page chrome — stays outside the printed sheet */}
      {!printMode && (
        <FormPageHeader
          patientName={patientName}
          mrn={patient?.mrn}
          status={form.status}
          onBack={onBack}
        />
      )}

      {/* The form itself — a single white sheet with margins on both sides */}
      <div
        className={`${SHEET} bg-white rounded-xl border border-slate-200 shadow-sm px-5 sm:px-8 py-6 space-y-3`}
      >
        <header className="text-center pb-1">
          <h1 className="text-[13px] font-bold uppercase tracking-[0.16em] text-slate-800">
            Patient Consent
          </h1>
          <span className="mx-auto mt-2 block h-0.5 w-14 rounded-full bg-indigo-300" />
        </header>

        {/* Shared identity strip: Patient / MRN / DOB / Caregiver / Date */}
        <FormIdentityStrip
          form={form}
          caregiverSlot={caregiverSlot}
          patientName={patientName || form.patientName}
          mrn={patient?.mrn}
          dateOfBirth={displayDateUS(
            patient?.dateOfBirth || form.patientDateOfBirth,
          )}
          dateLabel="Date"
          dateValue={displayDateUS(values.consentDate)}
          dateSlot={
            printMode ? null : (
              <div className="min-w-0">
                <div className="text-[7px] font-semibold uppercase tracking-wider text-slate-400">
                  Date
                </div>
                <InputDatePicker
                  name="consentDate"
                  formatSeparator="/"
                  allowManualEntry
                  compact
                  disabled={!canEdit}
                  value={formatDateForInput(values.consentDate) || ""}
                  onChange={(event) =>
                    setValue("consentDate", event.target.value)
                  }
                  max={CONSENT_DATE_MAX}
                  className="mt-0.5 min-w-0"
                  inputClassName="w-full h-4 text-[10px] font-bold bg-transparent border-0 p-0 outline-none"
                />
              </div>
            )
          }
        />

        <FormMetaRow form={form} printMode={printMode} />

        {!printMode && <LockedReasonBanner reason={permissions.lockedReason} />}

        <Section icon={MapPinIcon} title="Company & Location Details">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-x-3 gap-y-2">
            <Field label="Company Name" span="lg:col-span-3">
              <input
                type="text"
                className={fieldClass}
                value={values.companyName}
                disabled={!canEdit}
                onChange={(e) => setValue("companyName", e.target.value)}
              />
            </Field>
            <Field label="Company Address" span="lg:col-span-3">
              <input
                type="text"
                className={fieldClass}
                value={values.companyAddress}
                disabled={!canEdit}
                placeholder="Street address"
                onChange={(e) => setValue("companyAddress", e.target.value)}
              />
            </Field>
            <Field label="City" span="lg:col-span-2">
              <input
                type="text"
                className={fieldClass}
                value={values.city}
                disabled={!canEdit}
                onChange={(e) => setValue("city", e.target.value)}
              />
            </Field>
            <Field label="State" span="lg:col-span-2">
              <input
                type="text"
                className={fieldClass}
                value={values.state}
                disabled={!canEdit}
                onChange={(e) => setValue("state", e.target.value)}
              />
            </Field>
            <Field label="Zip" span="lg:col-span-2">
              <input
                type="text"
                className={fieldClass}
                value={values.zipCode}
                disabled={!canEdit}
                onChange={(e) => setValue("zipCode", e.target.value)}
              />
            </Field>
            <Field label="Phone" span="lg:col-span-3">
              <input
                type="tel"
                className={fieldClass}
                value={values.phone}
                disabled={!canEdit}
                placeholder="(000) 000-0000"
                onChange={(e) => setValue("phone", e.target.value)}
              />
            </Field>
            <Field label="Fax" span="lg:col-span-3">
              <input
                type="tel"
                className={fieldClass}
                value={values.fax}
                disabled={!canEdit}
                placeholder="(000) 000-0000"
                onChange={(e) => setValue("fax", e.target.value)}
              />
            </Field>
          </div>
        </Section>

        <Section icon={ShieldCheckIcon} title="Consents & Legal Authorizations">
          <SubHeading>Primary authorizations</SubHeading>
          <div className="space-y-1.5">
            {PRIMARY_AUTHORIZATIONS.map((item) => (
              <CheckboxCard
                key={item.key}
                item={item}
                checked={Boolean(values[item.key])}
                disabled={!canEdit}
                onChange={(checked) => setValue(item.key, checked)}
              />
            ))}

            {/* The ABN carries the billing choice, so the two stay together. */}
            <div
              className={`rounded-md border ${
                abnAccepted
                  ? "border-indigo-300 bg-indigo-50/40"
                  : "border-slate-200"
              }`}
            >
              <CheckboxCard
                item={ADVANCE_BENEFICIARY_NOTICE}
                checked={abnAccepted}
                disabled={!canEdit}
                highlight
                onChange={(checked) => {
                  setValue(ADVANCE_BENEFICIARY_NOTICE.key, checked);
                  // Declining the notice leaves no billing choice to make.
                  if (!checked) setValue("billingConfiguration", "");
                }}
              />
              {abnAccepted && (
                <div className="px-3 pb-3">
                  <div className="rounded border border-slate-200 bg-white px-3 py-2">
                    <SubHeading>Billing configuration</SubHeading>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                      {BILLING_CONFIGURATIONS.map((option) => {
                        const selected =
                          values.billingConfiguration === option.value;
                        return (
                          <label
                            key={option.value}
                            className={`flex items-center justify-center gap-2 rounded border px-2 py-1.5 text-[11px] font-semibold transition-colors ${
                              selected
                                ? "border-indigo-300 bg-indigo-50 text-indigo-700"
                                : "border-slate-200 text-slate-600 hover:border-slate-300"
                            } ${canEdit ? "cursor-pointer" : "cursor-default"}`}
                          >
                            <input
                              type="radio"
                              name="billingConfiguration"
                              className="h-3.5 w-3.5 shrink-0 border-slate-300 text-indigo-500 focus:ring-indigo-200 disabled:opacity-60"
                              checked={selected}
                              disabled={!canEdit}
                              onChange={() =>
                                setValue("billingConfiguration", option.value)
                              }
                            />
                            {option.label}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </Section>

        <Section icon={CalendarIcon} title="Proposed Visit Frequency">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-100">
                <th className="pb-1 text-left text-[9px] font-semibold uppercase tracking-wide text-slate-500">
                  Service Type
                </th>
                <th className="pb-1 text-left text-[9px] font-semibold uppercase tracking-wide text-slate-500 w-32">
                  Treatment Freq
                </th>
              </tr>
            </thead>
            <tbody>
              {VISIT_FREQUENCY_ROWS.map((row) => (
                <tr key={row.key} className="border-b border-slate-50">
                  <td className="py-1 pr-3 text-[11px] font-semibold text-slate-700">
                    {row.label}
                  </td>
                  <td className="py-1 w-32">
                    <input
                      type="text"
                      className={fieldClass}
                      value={values[row.key]}
                      disabled={!canEdit}
                      placeholder="—"
                      maxLength={60}
                      aria-label={`${row.label} treatment frequency`}
                      onChange={(e) => setValue(row.key, e.target.value)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>

        <Section
          icon={MedicalNotesIcon}
          title="Clinician-Patient Acknowledgements"
        >
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5">
            {ACKNOWLEDGEMENTS.map((item) => (
              <li key={item.key}>
                <label
                  className={`flex items-start gap-2 text-[11px] leading-snug ${
                    canEdit ? "cursor-pointer text-slate-700" : "text-slate-500"
                  }`}
                >
                  <input
                    type="checkbox"
                    className={checkboxClass}
                    checked={Boolean(values[item.key])}
                    disabled={!canEdit}
                    onChange={(e) => setValue(item.key, e.target.checked)}
                  />
                  <span>{item.label}</span>
                </label>
              </li>
            ))}
          </ul>
        </Section>

        {/* Signature sections close out every form, above the office review. */}
        <FormSignatureSections
          {...signatureProps}
          patientSignatureUrl={
            showPatientSignature ? signatureProps.patientSignatureUrl : null
          }
          patientNameValue={values.patientSignatureName}
          onPatientNameChange={(v) => setValue("patientSignatureName", v)}
          clinicianNameValue={values.clinicianSignatureName}
          onClinicianSign={handleClinicianSign}
          canSign={canSign}
          showClinicianSection={!permissions.hideClinicalSignature}
          printMode={printMode}
        />

        {isReviewer && !printMode && (
          <OfficeReviewSection
            value={values.reviewComments}
            onChange={(v) => setValue("reviewComments", v)}
          />
        )}

        {/* On paper the comments are always static text, never a textarea. */}
        {(!isReviewer || printMode) && (
          <OfficeCommentsNote comments={form.reviewComments} />
        )}
      </div>

      <FormActionBar
        permissions={permissions}
        status={form.status}
        canEdit={canEdit}
        saving={saving}
        onSubmit={submit}
        printMode={printMode}
      />
    </div>
  );
};

PatientConsentForm.propTypes = {
  patient: PropTypes.object,
  clinicalForm: PropTypes.object.isRequired,
  /** Present when opened from the picker before any server row exists. */
  draft: PropTypes.object,
  onBack: PropTypes.func.isRequired,
  /** Called with the new clinical-form id after the first draft save. */
  onCreated: PropTypes.func,
  /** Renders the sheet frozen and without chrome, for the print page. */
  printMode: PropTypes.bool,
  /** Fired once the form's own data has loaded. */
  onReady: PropTypes.func,
};

export default PatientConsentForm;
